import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { RoutePaths } from "../../../../enumStrings";
import {
  useCreateCmpNewsletterMutation,
  useArchiveCmpNewsletterRecipientGroupMutation,
  useGetChamberMemberProSettingsQuery,
  useGetCmpManageMembersQuery,
  useGetCmpNewsletterRecipientGroupsQuery,
  usePreviewCmpNewsletterRecipientsLazyQuery,
  useSendCmpNewsletterNowMutation,
} from "../../../../graphql/types";
import useGetChamberFromUserIdInAuth from "../../../../hooks/useGetChamberFromUserIdInAuth";
import {
  emailFormStateToInput,
  newsletterReturnToFormState,
  previewQueryToSummary,
  recipientGroupReturnToUiGroup,
} from "../emailBackendMappers";
import EmailForm from "../EmailForm";
import {
  EmailFormState,
  EmailAudienceMemberOption,
  buildDefaultCreateEmailForm,
  cloneEmailForm,
} from "../emailMockData";

const CreateEmail = () => {
  const navigate = useNavigate();
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";

  const { data: settingsData } =
    useGetChamberMemberProSettingsQuery({
      variables: { chamberId },
      skip: !chamberId,
    });
  const {
    data: groupsData,
    refetch: refetchGroups,
  } = useGetCmpNewsletterRecipientGroupsQuery({
    variables: { input: { chamberId, includeArchived: false } },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });
  const {
    data: membersData,
  } = useGetCmpManageMembersQuery({
    variables: { input: { chamberId } },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });
  const [createCmpNewsletter, { loading: creatingNewsletter }] =
    useCreateCmpNewsletterMutation();
  const [archiveCmpNewsletterRecipientGroup] =
    useArchiveCmpNewsletterRecipientGroupMutation();
  const [previewCmpNewsletterRecipients] =
    usePreviewCmpNewsletterRecipientsLazyQuery({
      fetchPolicy: "network-only",
    });
  const [sendCmpNewsletterNow, { loading: sendingNow }] =
    useSendCmpNewsletterNowMutation();

  const initialEmail = useMemo(
    () =>
      buildDefaultCreateEmailForm({
        chamberName: chamber?.name,
        senderEmail: settingsData?.getChamberMemberProSettings.email.senderEmail,
        replyToEmail:
          settingsData?.getChamberMemberProSettings.email.replyToEmail,
      }),
    [chamber?.name, settingsData?.getChamberMemberProSettings.email]
  );
  const recipientGroups = useMemo(
    () =>
      (groupsData?.getCmpNewsletterRecipientGroups.groups || []).map(
        recipientGroupReturnToUiGroup
      ),
    [groupsData?.getCmpNewsletterRecipientGroups.groups]
  );
  const memberOptions: EmailAudienceMemberOption[] = useMemo(
    () =>
      (membersData?.getCmpManageMembers.rows || []).map((member) => ({
        id: member.id,
        companyName: member.companyName,
        lifecycleStatus: member.lifecycleStatus,
        membershipTypeId: member.membershipTypeId || "",
        membershipTypeName: member.membershipTypeName || "",
        dateJoinedUtcMilli: member.dateJoinedUtcMilli,
        fullTimeEmployees: member.fullTimeEmployees,
        partTimeEmployees: member.partTimeEmployees,
        renewalDateUtcMilli: member.renewalDateUtcMilli,
        categoryId: member.categoryId,
      })),
    [membersData?.getCmpManageMembers.rows]
  );
  const isInitialLookupLoading = !chamberId;

  const submitEmail = async (
    email: EmailFormState,
    options?: { stayOnCurrentPage?: boolean }
  ) => {
    if (!chamberId) {
      throw new Error("Chamber context is still loading. Try again in a moment.");
    }

    const result = await createCmpNewsletter({
      variables: {
        input: {
          chamberId,
          data: emailFormStateToInput(email),
        },
      },
    });
    const savedEmail = result.data?.createCmpNewsletter.newsletter;

    if (!savedEmail?.id) {
      throw new Error("The email draft could not be saved.");
    }

    const savedFormEmail = newsletterReturnToFormState(savedEmail);

    if (!options?.stayOnCurrentPage) {
      navigate(RoutePaths.chamberAdmin_emails_update.replace(":emailId", savedEmail.id));
    }

    return savedFormEmail;
  };

  const sendNow = async (email: EmailFormState) => {
    if (!chamberId || !email.id) {
      throw new Error("Email context is still loading. Try again in a moment.");
    }

    const result = await sendCmpNewsletterNow({
      variables: {
        input: {
          chamberId,
          newsletterId: email.id,
          note: email.reviewNote || undefined,
        },
      },
    });
    const sentEmail = result.data?.sendCmpNewsletterNow.newsletter;

    if (!sentEmail) {
      throw new Error("The email could not be sent.");
    }

    return newsletterReturnToFormState(sentEmail);
  };

  const archiveRecipientGroup = async (groupId: string) => {
    if (!chamberId) {
      throw new Error("Chamber context is still loading. Try again in a moment.");
    }

    await archiveCmpNewsletterRecipientGroup({
      variables: { input: { chamberId, groupId } },
    });
    await refetchGroups();
  };

  const previewRecipients = async (email: EmailFormState) => {
    if (!chamberId) {
      throw new Error("Chamber context is still loading. Try again in a moment.");
    }

    const result = await previewCmpNewsletterRecipients({
      variables: {
        input: {
          chamberId,
          recipientSelection: emailFormStateToInput(email).recipientSelection!,
        },
      },
    });

    return previewQueryToSummary(result.data?.previewCmpNewsletterRecipients);
  };

  return (
    <EmailForm
      mode="create"
      initialEmail={cloneEmailForm(initialEmail)}
      recipientGroups={recipientGroups}
      memberOptions={memberOptions}
      isLoading={isInitialLookupLoading}
      isSaving={creatingNewsletter || sendingNow}
      onSubmit={submitEmail}
      onArchiveRecipientGroup={archiveRecipientGroup}
      onPreviewRecipients={previewRecipients}
      onSendNow={sendNow}
    />
  );
};

export default CreateEmail;
